/**
 * A janela de decisão — specs 0059 e 0086.
 *
 * **Por que existe.** As decisões mais delicadas do painel eram coletadas por
 * `window.prompt` e `window.confirm`: entregar o arquivo ao banco, descartá-lo,
 * cancelar uma fatura, fechar uma competência. A auditoria de 29/09/2026 contou
 * ~76 caixas do navegador. Três coisas que elas não fazem, e esta janela faz:
 *
 * 1. **Diz a consequência antes.** "Depois disso o arquivo não pode mais ser
 *    descartado" muda a decisão, e cabia mal numa linha de `confirm`.
 * 2. **Valida antes de enviar, junto do campo.** O `prompt` de data conferia o
 *    formato depois de fechado, e o texto digitado se perdia. Aqui o erro fica
 *    embaixo do campo, ligado a ele (`aria-describedby`), e o foco vai até lá.
 * 3. **Mostra o erro do servidor dentro de si**, e não no topo da página, atrás
 *    do fundo escuro.
 *
 * Desde a 0086 é a `Janela` da biblioteca: um visual só para toda decisão do
 * painel. O botão é escuro (`--acao-principal`) para o que faz o processo andar e
 * vermelho (`--acao-perigo`) só para o que destrói.
 */

import React, { useMemo, useRef, useState } from 'react';
import { Janela, CampoDaJanela } from './componentes';

export interface CampoDaDecisao {
  chave: string;
  rotulo: string;
  /**
   * `selecao` existe para eliminar classe de erro por construção: o prompt que
   * pedia "(MM/AAAA)" não conferia o formato, e o texto ia cru para o servidor.
   * Com opções, não há formato a errar.
   */
  tipo: 'texto' | 'data' | 'textarea' | 'selecao';
  opcoes?: string[];
  obrigatorio?: boolean;
  /** Mínimo de caracteres, para motivo que precisa dizer alguma coisa. */
  minimo?: number;
  /** Teto de caracteres que o destino aceita (a SEFAZ, 255 na justificativa). Mostra a contagem. */
  maximo?: number;
  valorInicial?: string;
  ajuda?: string;
  placeholder?: string;
}

interface Props {
  titulo: string;
  /** O que acontece se confirmar. Aparece em destaque, antes dos campos. */
  consequencia: React.ReactNode;
  /** Uma frase sobre o registro, abaixo do título (quem, qual competência, quanto). */
  contexto?: React.ReactNode;
  campos?: CampoDaDecisao[];
  rotuloConfirmar: string;
  /** Vermelho para o que destrói; escuro para o que faz o processo andar. */
  perigo?: boolean;
  ocupado: boolean;
  erro: string | null;
  /** O que fica registrado, no rodapé. */
  apoio?: React.ReactNode;
  onConfirmar: (valores: Record<string, string>) => void;
  onFechar: () => void;
}

/** O que falta num campo, em palavras — ou nada. */
function falta(c: CampoDaDecisao, valor: string): string | null {
  const v = valor.trim();
  if (c.obrigatorio && !v) return c.tipo === 'selecao' ? `Escolha ${c.rotulo.toLowerCase()}.` : `Preencha ${c.rotulo.toLowerCase()}.`;
  if (c.minimo && v.length < c.minimo) return `Escreva pelo menos ${c.minimo} caracteres (faltam ${c.minimo - v.length}).`;
  return null;
}

const ModalDecisao: React.FC<Props> = ({
  titulo, consequencia, contexto, campos = [], rotuloConfirmar, perigo, ocupado, erro, apoio,
  onConfirmar, onFechar,
}) => {
  const [valores, setValores] = useState<Record<string, string>>(
    () => Object.fromEntries(campos.map((c) => [c.chave, c.valorInicial || ''])));
  // Os erros só aparecem depois da primeira tentativa: gritar antes de a
  // pessoa digitar é ruído.
  const [tentou, setTentou] = useState(false);
  const refs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>>({});

  const erros = useMemo(() => Object.fromEntries(
    campos.map((c) => [c.chave, falta(c, valores[c.chave] || '')])), [campos, valores]);

  const confirmar = () => {
    const primeiro = campos.find((c) => erros[c.chave]);
    if (primeiro) {
      setTentou(true);
      refs.current[primeiro.chave]?.focus();
      return;
    }
    onConfirmar(valores);
  };

  const mudar = (chave: string, v: string) => setValores((atual) => ({ ...atual, [chave]: v }));

  return (
    <Janela
      aberta
      titulo={titulo}
      contexto={contexto}
      erro={erro}
      ocupado={ocupado}
      apoio={apoio}
      onFechar={onFechar}
      acoes={(
        <>
          <button type="button" className="fc-btn fc-btn-secundario" disabled={ocupado} onClick={onFechar}>
            Voltar
          </button>
          <button type="button" className={`fc-btn ${perigo ? 'fc-btn-perigo' : 'fc-btn-primario'}`}
            disabled={ocupado} onClick={confirmar}>
            {ocupado ? 'Registrando…' : rotuloConfirmar}
          </button>
        </>
      )}
    >
      <div className="fc-consequencia" role="note">
        <b>O que acontece</b>
        {/* div, não span: a consequência pode ter parágrafos e lista (0090). */}
        <div className="fc-consequencia-texto">{consequencia}</div>
      </div>

      {campos.map((c) => {
        const id = `decisao-${c.chave}`;
        const msg = tentou ? erros[c.chave] : null;
        const descricao = [msg && `${id}-erro`, c.ajuda && `${id}-ajuda`, c.maximo && `${id}-contagem`]
          .filter(Boolean).join(' ') || undefined;
        const comum = {
          id,
          ref: (el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null) => { refs.current[c.chave] = el; },
          value: valores[c.chave] || '',
          disabled: ocupado,
          'aria-invalid': msg ? true : undefined,
          'aria-describedby': descricao,
          maxLength: c.maximo,
        };
        return (
          <CampoDaJanela key={c.chave} largo rotulo={`${c.rotulo}${!c.obrigatorio && !c.minimo ? ' (opcional)' : ''}`}>
            {c.tipo === 'selecao' ? (
              <select {...comum} onChange={(e) => mudar(c.chave, e.target.value)}>
                {!c.valorInicial && <option value="">Escolha…</option>}
                {(c.opcoes || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : c.tipo === 'textarea' ? (
              <textarea {...comum} rows={3} placeholder={c.placeholder} onChange={(e) => mudar(c.chave, e.target.value)} />
            ) : (
              <input {...comum} type={c.tipo === 'data' ? 'date' : 'text'} placeholder={c.placeholder}
                onChange={(e) => mudar(c.chave, e.target.value)} />
            )}
            {msg && <small id={`${id}-erro`} className="fc-janela-erro-campo">{msg}</small>}
            {c.ajuda && <small id={`${id}-ajuda`} className="fc-janela-ajuda">{c.ajuda}</small>}
            {c.maximo && (
              <small id={`${id}-contagem`} className="fc-janela-ajuda">
                {(valores[c.chave] || '').length} de {c.maximo} caracteres
              </small>
            )}
          </CampoDaJanela>
        );
      })}
    </Janela>
  );
};

export default ModalDecisao;

// ── useDecisao ──────────────────────────────────────────────────────────────

export interface PedidoDeDecisao {
  titulo: string;
  consequencia: React.ReactNode;
  rotuloConfirmar: string;
  perigo?: boolean;
  contexto?: React.ReactNode;
  apoio?: React.ReactNode;
}

/**
 * O substituto do `window.confirm` (0086): `if (await confirmar({…}))` no lugar
 * de `if (window.confirm('…'))`, com a consequência escrita e a mesma janela do
 * resto do painel. O componente devolvido vai uma vez no JSX da tela.
 *
 *   const { janela, confirmar } = useDecisao();
 *   …
 *   if (!(await confirmar({ titulo: 'Sair sem salvar?', consequencia: '…',
 *     rotuloConfirmar: 'Sair sem salvar', perigo: true }))) return;
 *   …
 *   return <>{janela}…</>;
 */
export function useDecisao() {
  const [pedido, setPedido] = useState<(PedidoDeDecisao & { resolver: (sim: boolean) => void }) | null>(null);

  const confirmar = (p: PedidoDeDecisao) => new Promise<boolean>((resolver) => setPedido({ ...p, resolver }));

  const janela = pedido ? (
    <ModalDecisao
      titulo={pedido.titulo}
      consequencia={pedido.consequencia}
      contexto={pedido.contexto}
      apoio={pedido.apoio ?? null}
      rotuloConfirmar={pedido.rotuloConfirmar}
      perigo={pedido.perigo}
      ocupado={false}
      erro={null}
      onConfirmar={() => { pedido.resolver(true); setPedido(null); }}
      onFechar={() => { pedido.resolver(false); setPedido(null); }}
    />
  ) : null;

  return { janela, confirmar };
}
